import React, { useState, useEffect } from 'react';
import ToolLayout from '../components/ToolLayout';

interface UrlComponents {
  protocol: string;
  hostname: string;
  port: string;
  pathname: string;
  search: string;
  hash: string;
  origin: string;
  params: { [key: string]: string };
}

interface ShortUrl {
  id: string;
  originalUrl: string;
  shortCode: string;
  created: string;
  clicks: number;
}

const UrlAnalyzer: React.FC = () => {
  const [urlInput, setUrlInput] = useState('');
  const [components, setComponents] = useState<UrlComponents | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [shortUrls, setShortUrls] = useState<ShortUrl[]>([]);
  const [mode, setMode] = useState<'analyze' | 'shorten'>('analyze');

  useEffect(() => {
    // Load saved short URLs from localStorage
    const saved = localStorage.getItem('shortUrls');
    if (saved) {
      setShortUrls(JSON.parse(saved));
    }
  }, []);

  const saveShortUrls = (urls: ShortUrl[]) => {
    localStorage.setItem('shortUrls', JSON.stringify(urls));
    setShortUrls(urls);
  };

  const analyzeUrl = () => {
    setError('');
    setSuccess('');
    setComponents(null);

    if (!urlInput.trim()) {
      setError('Please enter a URL to analyze');
      return;
    }

    try {
      const url = new URL(urlInput);

      const params: { [key: string]: string } = {};
      url.searchParams.forEach((value, key) => {
        params[key] = value;
      });

      setComponents({
        protocol: url.protocol,
        hostname: url.hostname,
        port: url.port || '(default)',
        pathname: url.pathname,
        search: url.search,
        hash: url.hash,
        origin: url.origin,
        params
      });

      setSuccess('URL analyzed successfully! 🔍');
    } catch (err) {
      setError('Invalid URL: ' + (err as Error).message);
      setComponents(null);
    }
  };

  const generateShortCode = (): string => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const createShortUrl = () => {
    setError('');
    setSuccess('');

    if (!urlInput.trim()) {
      setError('Please enter a URL to shorten');
      return;
    }

    try {
      new URL(urlInput); // Validate URL

      // Check if URL already exists
      const existing = shortUrls.find(s => s.originalUrl === urlInput);
      if (existing) {
        setSuccess('URL already shortened! Using existing short code.');
        return;
      }

      const shortCode = generateShortCode();
      const newShortUrl: ShortUrl = {
        id: Date.now().toString(),
        originalUrl: urlInput,
        shortCode,
        created: new Date().toLocaleString(),
        clicks: 0
      };

      const updated = [newShortUrl, ...shortUrls];
      saveShortUrls(updated);
      setSuccess('Short URL created successfully! 🔗');
    } catch (err) {
      setError('Invalid URL: ' + (err as Error).message);
    }
  };

  const deleteShortUrl = (id: string) => {
    const updated = shortUrls.filter(s => s.id !== id);
    saveShortUrls(updated);
    setSuccess('Short URL deleted! 🗑️');
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess(`Copied ${label} to clipboard! 📋`);
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  const clearAll = () => {
    setUrlInput('');
    setComponents(null);
    setError('');
    setSuccess('');
  };

  const loadExample = () => {
    setUrlInput('https://example.com:8080/path/to/page?name=John&age=30#section1');
    setMode('analyze');
  };

  return (
    <ToolLayout
      title="URL Analyzer & Shortener"
      description="Phân tích URL components và tạo short links"
    >
      {/* Mode Selection */}
      <div className="url-mode-section">
        <div className="mode-tabs">
          <button
            className={`mode-tab ${mode === 'analyze' ? 'active' : ''}`}
            onClick={() => setMode('analyze')}
          >
            🔍 Analyze URL
          </button>
          <button
            className={`mode-tab ${mode === 'shorten' ? 'active' : ''}`}
            onClick={() => setMode('shorten')}
          >
            🔗 Shorten URL
          </button>
        </div>
      </div>

      {/* URL Input */}
      <div className="input-group">
        <label className="input-label">
          {mode === 'analyze' ? 'Enter URL to analyze:' : 'Enter URL to shorten:'}
        </label>
        <input
          type="text"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          placeholder="https://example.com/path?param=value"
          className="url-input"
          onKeyPress={(e) => e.key === 'Enter' && (mode === 'analyze' ? analyzeUrl() : createShortUrl())}
        />
      </div>

      {/* Action Buttons */}
      <div className="button-group">
        {mode === 'analyze' ? (
          <>
            <button className="action-button" onClick={analyzeUrl}>
              🔍 Analyze URL
            </button>
            <button className="action-button secondary" onClick={loadExample}>
              📝 Load Example
            </button>
          </>
        ) : (
          <button className="action-button" onClick={createShortUrl}>
            🔗 Create Short URL
          </button>
        )}
        <button className="action-button secondary" onClick={clearAll}>
          Clear
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* URL Components Display (Analyze Mode) */}
      {mode === 'analyze' && components && (
        <div className="url-components-section">
          <h3 className="components-title">🔗 URL Components:</h3>

          <div className="components-grid">
            <div className="component-item">
              <div className="component-header">
                <span className="component-label">Protocol:</span>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(components.protocol, 'Protocol')}
                >
                  📋
                </button>
              </div>
              <code className="component-value">{components.protocol}</code>
              <div className="component-description">The protocol scheme (http, https, ftp, etc.)</div>
            </div>

            <div className="component-item">
              <div className="component-header">
                <span className="component-label">Hostname:</span>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(components.hostname, 'Hostname')}
                >
                  📋
                </button>
              </div>
              <code className="component-value">{components.hostname}</code>
              <div className="component-description">The domain name or IP address</div>
            </div>

            <div className="component-item">
              <div className="component-header">
                <span className="component-label">Port:</span>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(components.port, 'Port')}
                >
                  📋
                </button>
              </div>
              <code className="component-value">{components.port}</code>
              <div className="component-description">Port number (80 for HTTP, 443 for HTTPS)</div>
            </div>

            <div className="component-item">
              <div className="component-header">
                <span className="component-label">Pathname:</span>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(components.pathname, 'Pathname')}
                >
                  📋
                </button>
              </div>
              <code className="component-value">{components.pathname || '/'}</code>
              <div className="component-description">The path to the resource</div>
            </div>

            <div className="component-item">
              <div className="component-header">
                <span className="component-label">Search/Query:</span>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(components.search, 'Query string')}
                >
                  📋
                </button>
              </div>
              <code className="component-value">{components.search || '(none)'}</code>
              <div className="component-description">Query string with parameters</div>
            </div>

            <div className="component-item">
              <div className="component-header">
                <span className="component-label">Hash/Fragment:</span>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(components.hash, 'Hash')}
                >
                  📋
                </button>
              </div>
              <code className="component-value">{components.hash || '(none)'}</code>
              <div className="component-description">Fragment identifier (anchor)</div>
            </div>

            <div className="component-item">
              <div className="component-header">
                <span className="component-label">Origin:</span>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(components.origin, 'Origin')}
                >
                  📋
                </button>
              </div>
              <code className="component-value">{components.origin}</code>
              <div className="component-description">Protocol + hostname + port</div>
            </div>
          </div>

          {/* Query Parameters */}
          {Object.keys(components.params).length > 0 && (
            <div className="params-section">
              <h4 className="params-title">📋 Query Parameters ({Object.keys(components.params).length}):</h4>
              <div className="params-grid">
                {Object.entries(components.params).map(([key, value]) => (
                  <div key={key} className="param-item">
                    <div className="param-key">{key}</div>
                    <div className="param-separator">=</div>
                    <div className="param-value">{value}</div>
                    <button
                      className="copy-btn"
                      onClick={() => copyToClipboard(`${key}=${value}`, 'Parameter')}
                    >
                      📋
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Short URLs Display (Shorten Mode) */}
      {mode === 'shorten' && shortUrls.length > 0 && (
        <div className="short-urls-section">
          <h3 className="short-urls-title">🔗 Your Short URLs ({shortUrls.length}):</h3>
          <div className="short-urls-list">
            {shortUrls.map((shortUrl) => (
              <div key={shortUrl.id} className="short-url-item">
                <div className="short-url-header">
                  <div className="short-code-badge">
                    short/{shortUrl.shortCode}
                  </div>
                  <div className="short-url-meta">
                    {shortUrl.created} • {shortUrl.clicks} clicks
                  </div>
                </div>
                <div className="short-url-original">
                  <strong>Original:</strong> {shortUrl.originalUrl}
                </div>
                <div className="short-url-actions">
                  <button
                    className="action-button"
                    onClick={() => copyToClipboard(`${window.location.origin}/s/${shortUrl.shortCode}`, 'Short URL')}
                    style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }}
                  >
                    📋 Copy Short URL
                  </button>
                  <button
                    className="action-button secondary"
                    onClick={() => copyToClipboard(shortUrl.originalUrl, 'Original URL')}
                    style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }}
                  >
                    📋 Copy Original
                  </button>
                  <button
                    className="action-button secondary"
                    onClick={() => deleteShortUrl(shortUrl.id)}
                    style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="url-info-section">
        <h4 className="url-info-title">📖 URL Structure Guide:</h4>
        <div className="url-info-content">
          <div className="url-structure-example">
            <code className="structure-code">
              protocol://hostname:port/pathname?search#hash
            </code>
            <div className="structure-breakdown">
              <div><strong>protocol</strong> - https</div>
              <div><strong>hostname</strong> - example.com</div>
              <div><strong>port</strong> - 8080 (optional)</div>
              <div><strong>pathname</strong> - /path/to/page</div>
              <div><strong>search</strong> - ?key=value&amp;foo=bar</div>
              <div><strong>hash</strong> - #section</div>
            </div>
          </div>

          <strong style={{ display: 'block', marginTop: '1rem' }}>Features:</strong>
          <ul className="url-features-list">
            <li><strong>URL Analysis:</strong> Parse và hiển thị tất cả components của URL</li>
            <li><strong>Query Parameters:</strong> Extract và display parameters dạng key-value</li>
            <li><strong>URL Shortener:</strong> Tạo short links và lưu trữ locally</li>
            <li><strong>Copy to Clipboard:</strong> Copy bất kỳ component nào với 1 click</li>
          </ul>

          <div className="url-info-note">
            <strong>Note:</strong> Short URLs được lưu trữ trong browser localStorage và chỉ hoạt động locally.
            Để tạo public short URLs, bạn cần một backend service như bit.ly, tinyurl.com, etc.
          </div>
        </div>
      </div>

      <style>{`
        .url-mode-section {
          margin-bottom: 2rem;
        }

        .mode-tabs {
          display: flex;
          gap: 0.5rem;
          border-bottom: 2px solid var(--border-color);
        }

        .mode-tab {
          padding: 0.75rem 1.5rem;
          background: transparent;
          border: none;
          border-bottom: 2px solid transparent;
          cursor: pointer;
          color: var(--text-secondary);
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .mode-tab:hover {
          color: var(--text-primary);
          background: var(--bg-secondary);
        }

        .mode-tab.active {
          color: var(--accent-primary);
          border-bottom-color: var(--accent-primary);
        }

        .url-input {
          width: 100%;
          padding: 0.75rem;
          border: 2px solid var(--border-color);
          border-radius: 8px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
          font-size: 0.9375rem;
          transition: all 0.3s ease;
        }

        .url-input:focus {
          outline: none;
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 3px var(--accent-light);
        }

        .url-input::placeholder {
          color: var(--text-tertiary);
        }

        .url-components-section {
          padding: 1.5rem;
          background: var(--bg-secondary);
          border: 2px solid var(--border-color);
          border-radius: 12px;
          margin-top: 2rem;
        }

        .components-title {
          margin: 0 0 1.5rem 0;
          color: var(--text-primary);
          font-size: 1.25rem;
          font-weight: 600;
        }

        .components-grid {
          display: grid;
          gap: 1rem;
        }

        .component-item {
          padding: 1rem;
          background: var(--card-bg);
          border: 2px solid var(--border-color);
          border-radius: 8px;
        }

        .component-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .component-label {
          font-weight: 600;
          color: var(--text-secondary);
          font-size: 0.9rem;
        }

        .copy-btn {
          background: transparent;
          border: none;
          cursor: pointer;
          font-size: 1rem;
          padding: 0.25rem 0.5rem;
          opacity: 0.7;
          transition: opacity 0.2s;
        }

        .copy-btn:hover {
          opacity: 1;
        }

        .component-value {
          display: block;
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
          font-size: 0.95rem;
          color: var(--accent-primary);
          word-break: break-all;
          margin-bottom: 0.5rem;
        }

        .component-description {
          font-size: 0.85rem;
          color: var(--text-tertiary);
        }

        .params-section {
          margin-top: 2rem;
          padding: 1.5rem;
          background: var(--card-bg);
          border: 2px solid var(--border-color);
          border-radius: 8px;
        }

        .params-title {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
          font-weight: 600;
        }

        .params-grid {
          display: grid;
          gap: 0.75rem;
        }

        .param-item {
          display: grid;
          grid-template-columns: auto auto 1fr auto;
          gap: 0.75rem;
          align-items: center;
          padding: 0.75rem;
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 6px;
        }

        .param-key {
          font-weight: 600;
          color: var(--text-primary);
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
        }

        .param-separator {
          color: var(--text-tertiary);
        }

        .param-value {
          color: var(--accent-primary);
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
          word-break: break-all;
        }

        .short-urls-section {
          margin-top: 2rem;
          padding: 1.5rem;
          background: var(--bg-secondary);
          border: 2px solid var(--border-color);
          border-radius: 12px;
        }

        .short-urls-title {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
          font-size: 1.25rem;
          font-weight: 600;
        }

        .short-urls-list {
          display: grid;
          gap: 1rem;
        }

        .short-url-item {
          padding: 1rem;
          background: var(--card-bg);
          border: 2px solid var(--border-color);
          border-radius: 8px;
        }

        .short-url-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }

        .short-code-badge {
          padding: 0.5rem 1rem;
          background: var(--accent-light);
          border: 2px solid var(--accent-primary);
          border-radius: 6px;
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
          font-weight: 600;
          color: var(--accent-primary);
        }

        .short-url-meta {
          font-size: 0.85rem;
          color: var(--text-tertiary);
        }

        .short-url-original {
          padding: 0.75rem;
          background: var(--bg-primary);
          border-radius: 6px;
          margin-bottom: 0.75rem;
          word-break: break-all;
          color: var(--text-secondary);
        }

        .short-url-actions {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .url-info-section {
          margin-top: 2rem;
          padding: 1.5rem;
          background: var(--bg-secondary);
          border-radius: 12px;
          border: 2px solid var(--accent-primary);
        }

        .url-info-title {
          margin: 0 0 1rem 0;
          color: var(--accent-primary);
          font-weight: 600;
        }

        .url-info-content {
          color: var(--text-secondary);
          font-size: 0.9rem;
          line-height: 1.6;
        }

        .url-structure-example {
          padding: 1rem;
          background: var(--card-bg);
          border-radius: 8px;
          margin-bottom: 1rem;
        }

        .structure-code {
          display: block;
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
          color: var(--accent-primary);
          font-size: 1rem;
          margin-bottom: 1rem;
          word-break: break-all;
        }

        .structure-breakdown {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 0.5rem;
          font-size: 0.85rem;
        }

        .structure-breakdown strong {
          color: var(--text-primary);
        }

        .url-features-list {
          margin: 0.5rem 0;
          padding-left: 1.5rem;
        }

        .url-features-list li {
          margin-bottom: 0.5rem;
        }

        .url-info-note {
          margin-top: 1rem;
          padding: 1rem;
          background: var(--card-bg);
          border-left: 3px solid var(--accent-primary);
          border-radius: 4px;
          font-size: 0.875rem;
        }

        @media (max-width: 768px) {
          .mode-tabs {
            flex-direction: column;
          }

          .mode-tab {
            width: 100%;
            text-align: left;
          }

          .param-item {
            grid-template-columns: 1fr;
          }

          .short-url-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }

          .short-url-actions {
            flex-direction: column;
          }

          .short-url-actions button {
            width: 100%;
          }
        }
      `}</style>
    </ToolLayout>
  );
};

export default UrlAnalyzer;